/**
 * piecePaths — Pure SVG path data constants for standard chess pieces.
 *
 * Each piece is defined as an array of SVG path strings (d attributes).
 * These are sized for a 24x24 viewBox and will scale proportionally.
 *
 * Design: Classic Staunton-inspired forms, clean lines, no gradients.
 * Colors are applied via CSS variables / Tailwind classes in Piece.tsx.
 */

export interface PiecePaths {
  [key: string]: string[];
}

/** White piece paths (filled with cream/white, dark stroke) */
export const WHITE_PIECE_PATHS: PiecePaths = {
  K: [
    // King: crown base + cross
    'M12 4c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 3c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z',
    // Cross top
    'M12 2c-.55 0-1 .45-1 1s.45 1 1 1 1-.45 1-1-.45-1-1-1zm0 18c-.55 0-1 .45-1 1s.45 1 1 1 1-.45 1-1-.45-1-1-1z',
    // Crown sides
    'M4.93 12c-.78.52-1.23 1.48-.9 2.44.26.75.89 1.38 1.65 1.65.77.26 1.73-.18 2.44-.9l4.4-4.4c.37-.37.58-.88.58-1.41s-.21-1.04-.58-1.41L7.37 2.9c-.7-.7-1.66-.7-2.36 0-.7.7-.7 1.66 0 2.36l4.4 4.4c.37.37.58.88.58 1.41s-.21 1.04-.58 1.41l-4.4 4.4z',
    // Crown base band
    'M6 10h12v2H6z',
  ],
  Q: [
    // Queen: crown with ball top
    'M12 3c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 3c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z',
    // Crown spikes
    'M5.5 11c-.83 0-1.5.67-1.5 1.5S4.67 14 5.5 14s1.5-.67 1.5-1.5S6.33 11 5.5 11zm7 0c-.83 0-1.5.67-1.5 1.5S11.67 14 12.5 14s1.5-.67 1.5-1.5S13.33 11 12.5 11z',
    // Crown center
    'M12 11c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z',
    // Crown base
    'M4 15h16v2H4z',
    // Body
    'M8 17v4h8v-4H8z',
  ],
  R: [
    // Rook: battlement top
    'M4 4h16v2H4zm0 3h16v1H4zm0 3h16v1H4zm0 3h16v1H4zm0 3h16v2H4z',
    // Body
    'M6 8v10h12V8H6z',
  ],
  B: [
    // Bishop: mitre shape
    'M12 2c1.1 0 2 .9 2 2s-.9 2-2 2-2-.9-2-2 .9-2 2-2zm0 3c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z',
    // Mitre cut
    'M7.5 12c0-1.1.9-2 2-2s2 .9 2 2-.9 2-2 2-2-.9-2-2zm4.5 0c0-1.1.9-2 2-2s2 .9 2 2-.9 2-2 2-2-.9-2-2z',
    // Body
    'M7 14v6h10v-6H7z',
  ],
  N: [
    // Knight: horse head profile (simplified Staunton)
    'M14.5 4.5c-.83 0-1.5.67-1.5 1.5s.67 1.5 1.5 1.5 1.5-.67 1.5-1.5-.67-1.5-1.5-1.5zm-7 0c-.83 0-1.5.67-1.5 1.5s.67 1.5 1.5 1.5 1.5-.67 1.5-1.5-.67-1.5-1.5-1.5z',
    // Mane
    'M7.5 6c0-1.66 1.34-3 3-3s3 1.34 3 3-1.34 3-3 3-3-1.34-3-3zm7 0c0-1.66 1.34-3 3-3s3 1.34 3 3-1.34 3-3 3-3-1.34-3-3z',
    // Neck
    'M12 9c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z',
    // Base
    'M6 15h12v2H6z',
  ],
  P: [
    // Pawn: classic shape
    'M12 4c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z',
    'M8 8h8v1H8z',
    'M10 9v6h4v-6h-4z',
  ],
};

/** Black piece paths (filled with dark charcoal, lighter stroke) */
export const BLACK_PIECE_PATHS: PiecePaths = {
  K: [
    'M12 4c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 3c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z',
    'M12 2c-.55 0-1 .45-1 1s.45 1 1 1 1-.45 1-1-.45-1-1-1zm0 18c-.55 0-1 .45-1 1s.45 1 1 1 1-.45 1-1-.45-1-1-1z',
    'M4.93 12c-.78.52-1.23 1.48-.9 2.44.26.75.89 1.38 1.65 1.65.77.26 1.73-.18 2.44-.9l4.4-4.4c.37-.37.58-.88.58-1.41s-.21-1.04-.58-1.41L7.37 2.9c-.7-.7-1.66-.7-2.36 0-.7.7-.7 1.66 0 2.36l4.4 4.4c.37.37.58.88.58 1.41s-.21 1.04-.58 1.41l-4.4 4.4z',
    'M6 10h12v2H6z',
  ],
  Q: [
    'M12 3c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 3c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z',
    'M5.5 11c-.83 0-1.5.67-1.5 1.5S4.67 14 5.5 14s1.5-.67 1.5-1.5S6.33 11 5.5 11zm7 0c-.83 0-1.5.67-1.5 1.5S11.67 14 12.5 14s1.5-.67 1.5-1.5S13.33 11 12.5 11z',
    'M12 11c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z',
    'M4 15h16v2H4z',
    'M8 17v4h8v-4H8z',
  ],
  R: [
    'M4 4h16v2H4zm0 3h16v1H4zm0 3h16v1H4zm0 3h16v1H4zm0 3h16v2H4z',
    'M6 8v10h12V8H6z',
  ],
  B: [
    'M12 2c1.1 0 2 .9 2 2s-.9 2-2 2-2-.9-2-2 .9-2 2-2zm0 3c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z',
    'M7.5 12c0-1.1.9-2 2-2s2 .9 2 2-.9 2-2 2-2-.9-2-2zm4.5 0c0-1.1.9-2 2-2s2 .9 2 2-.9 2-2 2-2-.9-2-2z',
    'M7 14v6h10v-6H7z',
  ],
  N: [
    'M14.5 4.5c-.83 0-1.5.67-1.5 1.5s.67 1.5 1.5 1.5 1.5-.67 1.5-1.5-.67-1.5-1.5-1.5zm-7 0c-.83 0-1.5.67-1.5 1.5s.67 1.5 1.5 1.5 1.5-.67 1.5-1.5-.67-1.5-1.5-1.5z',
    'M7.5 6c0-1.66 1.34-3 3-3s3 1.34 3 3-1.34 3-3 3-3-1.34-3-3zm7 0c0-1.66 1.34-3 3-3s3 1.34 3 3-1.34 3-3 3-3-1.34-3-3z',
    'M12 9c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z',
    'M6 15h12v2H6z',
  ],
  P: [
    'M12 4c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z',
    'M8 8h8v1H8z',
    'M10 9v6h4v-6h-4z',
  ],
};

/**
 * Get the SVG path array for a given piece type and color.
 * The path geometry is identical; only CSS colors differ.
 */
export function getPiecePaths(type: 'K' | 'Q' | 'R' | 'B' | 'N' | 'P', color: 'white' | 'black'): string[] {
  const paths = color === 'white' ? WHITE_PIECE_PATHS[type] : BLACK_PIECE_PATHS[type];
  return paths ?? [];
}